import React, { useEffect } from 'react';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import DashboardLayout from '../../domains/dashboard/components/dashboard-layout';
import AboutUsPage from '../../domains/about-us';
import MeetPage from '../../domains/meet';
import {
  PublicMentorProfilePage,
  PublicStudentProfilePage,
} from '../../domains/public';
import ChangelogPage from '../../domains/changelog';
import PublicOfferPage from '../../domains/legal';
import { loadLocalStore } from '../reducers/authorization-slice';
import { paths, roles, sharedPublicPaths } from './paths';
import { PublicMentorRedirect, routesByRole } from './routes';
import RoutesByRole from './RoutesByRole';
import { useAppRole } from './useRolePath';
import type { AppRoute } from './types';

function isAuthRoute(route: string) {
  return route.startsWith('/auth');
}

function LegacyClientRedirect() {
  const { pathname, search, hash } = useLocation();
  const next = pathname.replace(/^\/client(?=\/|$)/, '/student');
  return <Navigate to={`${next}${search}${hash}`} replace />;
}

const sharedPublicPageByPath: Record<string, React.ReactNode> = {
  [paths.aboutUs]: <AboutUsPage />,
  [paths.meet]: <MeetPage />,
  [paths.changelog]: <ChangelogPage />,
  [paths.publicOffer]: <PublicOfferPage />,
};

export default function AppRouter() {
  const dispatch = useDispatch();
  const role = useAppRole();

  useEffect(() => {
    dispatch(loadLocalStore());
  }, [dispatch]);

  const publicGuestRoutes = routesByRole.none.filter(
    (route) => !isAuthRoute(route.route),
  );
  const authGuestRoutes = routesByRole.none.filter((route) =>
    isAuthRoute(route.route),
  );

  return (
    <BrowserRouter>
      <Routes>
        <Route path="/sign-in" element={<Navigate to={paths.signIn} replace />} />
        <Route path="/sign-up" element={<Navigate to={paths.signUp} replace />} />
        <Route
          path="/forgot-password"
          element={<Navigate to={paths.forgotPassword} replace />}
        />
        <Route path="/client" element={<LegacyClientRedirect />} />
        <Route path="/client/*" element={<LegacyClientRedirect />} />
        {role === roles.NONE ? (
          <>
            {publicGuestRoutes.map((route: AppRoute) => (
              <Route
                key={route.route}
                path={route.route}
                element={route.element}
              />
            ))}
            {authGuestRoutes.map((route: AppRoute) => (
              <Route
                key={route.route}
                path={route.route}
                element={route.element}
              />
            ))}
            <Route path="*" element={<Navigate to={paths.signIn} replace />} />
          </>
        ) : (
          <>
            {sharedPublicPaths.map((path) => (
              <Route
                key={path}
                path={path}
                element={
                  <DashboardLayout>{sharedPublicPageByPath[path]}</DashboardLayout>
                }
              />
            ))}
            <Route
              path="/public/mentor/:slug"
              element={
                <DashboardLayout>
                  <PublicMentorProfilePage />
                </DashboardLayout>
              }
            />
            <Route path="/mentors/:slug" element={<PublicMentorRedirect />} />
            <Route
              path="/students/:slug"
              element={
                <DashboardLayout>
                  <PublicStudentProfilePage />
                </DashboardLayout>
              }
            />
            <Route
              path="*"
              element={
                <DashboardLayout>
                  <RoutesByRole />
                </DashboardLayout>
              }
            />
          </>
        )}
      </Routes>
    </BrowserRouter>
  );
}
